import { useEffect, useState, useSyncExternalStore } from 'react';

export function cx(...values: unknown[]): string {
  return values.filter((v): v is string => typeof v === 'string' && v.length > 0).join(' ');
}

export function useMediaQuery(query: string): boolean {
  const get = () => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(query).matches : false);
  const [matches, setMatches] = useState(get);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, [query]);
  return matches;
}

export type ThemeMode = 'light' | 'dark' | 'system';
const THEME_KEY = 'serota.theme';

function systemTheme(): 'light' | 'dark' {
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches
    ? 'dark'
    : 'light';
}

// Tema tek kaynaktan: tüm bileşenler (üst bar anahtarı, harita, grafikler) aynı modu görür.
const themeListeners = new Set<() => void>();
let themeMode: ThemeMode | null = null;

function readMode(): ThemeMode {
  if (themeMode) return themeMode;
  try {
    themeMode = (localStorage.getItem(THEME_KEY) as ThemeMode | null) ?? 'light';
  } catch {
    themeMode = 'light';
  }
  return themeMode;
}

function subscribeTheme(fn: () => void): () => void {
  themeListeners.add(fn);
  const mql = typeof window !== 'undefined' ? window.matchMedia?.('(prefers-color-scheme: dark)') : undefined;
  mql?.addEventListener('change', fn);
  return () => {
    themeListeners.delete(fn);
    mql?.removeEventListener('change', fn);
  };
}

function setThemeMode(m: ThemeMode): void {
  themeMode = m;
  try {
    localStorage.setItem(THEME_KEY, m);
  } catch {
    /* storage may be unavailable */
  }
  themeListeners.forEach((l) => l());
}

/** Applies data-theme on <html>; remembers the choice per browser (convenience only). */
export function useThemeMode(): [ThemeMode, (m: ThemeMode) => void, 'light' | 'dark'] {
  const mode = useSyncExternalStore(subscribeTheme, readMode, () => 'light' as ThemeMode);
  const system = useSyncExternalStore(subscribeTheme, systemTheme, () => 'light' as const);
  const resolved = mode === 'system' ? system : mode;
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', resolved);
  }, [resolved]);
  return [mode, setThemeMode, resolved];
}

export function useOnline(): boolean {
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);
  return online;
}
